import { Button } from '@repo/ui/components/button';
import { useForm } from '@repo/ui/hooks/use-form';
import { CreatePlayer as CreatePlayerType, Player } from '@repo/shared-types';
import { useAsync } from '@repo/ui/hooks/use-async';
import { useRevalidator } from 'react-router';
import { Input } from '@repo/ui/components/form/input';
import { playerApi } from '@repo/api-client';
import { useCurrentClub } from '@/hooks/use-current-club';
import { useState, useRef, useEffect } from 'react';
import { getFormattedPhone } from '@repo/ui/lib/phone';
import { countries } from '@repo/ui/lib/countries';
import { Select } from '@repo/ui/components/form/select';

function SearchByPhone({
  onSuccess,
  onNotFound,
}: {
  onSuccess: (player: Player) => void;
  onNotFound: (phone: string, countryCode: string) => void;
}) {
  const club = useCurrentClub();
  const [player, setPlayer] = useState<Player | null>(null);
  const [isRegistered, setIsRegistered] = useState(false);
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const { execute: onSubmit, loading } = useAsync(async () => {
    const response = await playerApi.searchByPhone(club.id, values.phone);
    if (response.results.length === 0) {
      onNotFound(values.phone, values.countryCode);
    } else {
      // Player is already registered in the club
      if (response.results[0].clubPlayers.length > 0) {
        setIsRegistered(true);
        setPlayer(null);
      } else {
        setPlayer(response.results[0]);
      }
    }
  });
  const { ref, values, errors } = useForm({
    initialValues: {
      phone: '',
      countryCode: club.country || countries[0].value,
    },
    onSubmit,
  });

  useEffect(() => {
    // Focus and select the phone input when the component mounts
    if (phoneInputRef.current) {
      phoneInputRef.current.focus();
      phoneInputRef.current.select();
    }
  }, []);
  if (player) {
    return (
      <form ref={ref} className="w-full flex flex-col gap-4">
        <span>El jugador {player.name} ya existe, desea registrarlo como jugador del club?</span>
        <Button type="button" onClick={() => onSuccess(player)} loading={loading}>
          Si, registrarlo
        </Button>
        <Button type="button" onClick={() => setPlayer(null)} loading={loading}>
          Volver a buscar
        </Button>
      </form>
    );
  }
  return (
    <form ref={ref} className="w-full flex flex-col gap-4">
      {isRegistered && <span>El jugador ya está registrado en el club</span>}
      <div className="flex items-center gap-2">
        <Select
          className="w-10!"
          options={countries.map((country) => ({
            value: country.value,
            label: country.flag,
          }))}
          name="countryCode"
          label="País"
        />
        <Input
          label="Teléfono"
          type="number"
          name="phone"
          required
          errors={errors}
          onChange={() => setIsRegistered(false)}
          inputRef={phoneInputRef}
        />
      </div>
      <Button type="submit" loading={loading}>
        Continuar
      </Button>
    </form>
  );
}

function CreatePlayerForm({
  phone,
  countryCode,
  onBack,
  onClose,
}: {
  phone: string;
  countryCode: string;
  onBack: () => void;
  onClose: () => void;
}) {
  const club = useCurrentClub();
  const revalidator = useRevalidator();

  const { execute: onSubmit } = useAsync(async () => {
    const finalValues = {
      ...values,
      phone: getFormattedPhone(values.phone, values.countryCode),
      countryCode: undefined,
    };
    await playerApi.createPlayer(club.id, finalValues);
    onBack();
    onClose();
    revalidator.revalidate();
  });

  const { ref, values, errors } = useForm<CreatePlayerType>({
    initialValues: {
      countryCode,
      phone,
      lastName: '',
      name: '',
    },
    onSubmit,
  });
  return (
    <form ref={ref} className="w-full flex flex-col gap-4">
      <Input label="Nombre" type="text" name="name" required errors={errors} />
      <Input label="Apellido" type="text" name="lastName" required errors={errors} />
      <div className="flex items-center gap-2">
        <Select
          className="w-10!"
          options={countries.map((country) => ({
            value: country.value,
            label: country.flag,
          }))}
          name="countryCode"
          label="País"
        />
        <Input label="Teléfono" type="number" name="phone" required errors={errors} value={phone} />
      </div>
      <Button type="submit">Crear</Button>
      <Button type="button" onClick={onBack}>
        Volver a buscar
      </Button>
    </form>
  );
}

export function CreatePlayer({ onClose }: { onClose: () => void }) {
  const club = useCurrentClub();
  const revalidator = useRevalidator();
  const [step, setStep] = useState<'search' | 'create'>('search');

  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState('');

  if (step === 'search') {
    return (
      <SearchByPhone
        onSuccess={async (player) => {
          await playerApi.registerPlayer(club.id, player.id);
          onClose();
          revalidator.revalidate();
        }}
        onNotFound={(phone, countryCode) => {
          setCountryCode(countryCode);
          setPhone(phone);
          setStep('create');
        }}
      />
    );
  }

  return (
    <CreatePlayerForm
      phone={phone}
      countryCode={countryCode}
      onBack={() => setStep('search')}
      onClose={onClose}
    />
  );
}
